import classNames from 'classnames';
import uniq from 'lodash/uniq';
import * as React from 'react';
import { useClickAway, useMount } from 'react-use';
import Tippy from '@tippyjs/react';

import { AdminUserRoleEnum, SalesRepUserRoleEnum } from '__generated-api__';
import { AuthStatus, useAuth } from 'auth';
import Icon from 'components/icon';
import { ProductIconLabels } from 'components/ProductIconLabels';
import wordpressData from 'data';
import { ProductHeroGallery } from 'my-account/components/ProductHero/Gallery';
import { ProductHeroPrice } from 'my-account/components/ProductHero/Price';
import { AddProductToQuote } from 'my-account/components/ProductHero/Quote';
import SpriteIcon from 'search/components/sprite-icon';
import { compareFinishes, compareShowerTrimModifierId } from 'utils/sort';

const sthpUndefinedAttributeKey = '__undefined__' as const;
const sthpShowerTrimModifierIdNullKey = '__null__' as const;

interface ISthpMultivariantAttributes {
  flowRateId: string | null | typeof sthpUndefinedAttributeKey;
  finish: string | null | typeof sthpUndefinedAttributeKey;
  showerTrimModifierId: string | null | typeof sthpUndefinedAttributeKey;
  isTestCap: boolean;
}

function getMultivariant(data: Symmons.ProductData, flowRateId: string | null, finish: string | null) {
  // find the best sellable product to switch to

  const products = data.multivariants.filter((product) => product.flowRateId === flowRateId);

  if (!products.length) {
    return null;
  }

  return products.find((product) => product.finish === finish) ?? products[0];
}

function sthpGetExactMultivariant(data: Symmons.ProductData, attributes: ISthpMultivariantAttributes) {
  return (
    data.multivariants.find((product) => {
      return (
        product.flowRateId === attributes.flowRateId &&
        product.finish === attributes.finish &&
        product.showerTrimModifierId === attributes.showerTrimModifierId &&
        product.isTestCap === attributes.isTestCap
      );
    }) || null
  );
}

function sthpGetPartialMultivariant(data: Symmons.ProductData, attributes: ISthpMultivariantAttributes) {
  const keys = ['flowRateId', 'showerTrimModifierId', 'finish', 'isTestCap'] as const;

  return (
    data.multivariants.find((product) => {
      for (const key of keys) {
        if (attributes[key] !== sthpUndefinedAttributeKey && attributes[key] !== product[key]) {
          return false;
        }
      }

      return true;

      // const matches = keys
      //   .map((key) => [key, attributes[key]] as const)
      //   .reduce((accumulator, [key, value]) => {
      //     if (value === sthpUndefinedAttributeKey || value === product[key]) {
      //       accumulator++;
      //     }

      //     return accumulator;
      //   }, 0);

      // return matches === keys.length;
    }) || null
  );
}

function sthpGetDistinctFinishVariants(data: Symmons.ProductData) {
  const distinctVariants = Object.values(
    data.multivariants
      .filter((product): product is Symmons.Product & { finish: string } => typeof product.finish === 'string')
      .reduce((accumulator, product) => {
        accumulator[product.finish] ??= product;
        return accumulator;
      }, {} as Record<string, Symmons.Product & { finish: string }>)
  );

  distinctVariants.sort((product1, product2) => compareFinishes(product1.finish, product2.finish));

  return distinctVariants;
}

function getCurrentFlowRateVariants(data: Symmons.ProductData, currentProduct: Symmons.Product) {
  const flowRateVariants = uniq(
    data.multivariants
      .filter((product): product is Symmons.Product & { flowRateId: string } => product.flowRateId !== null)
      .map((product) => product.flowRateId)
  )
    // get a variant for each flow rate ID
    .reduce((accumulator, flowRateId) => {
      const flowRateVariant = getMultivariant(data, flowRateId, currentProduct.finish);
      flowRateVariant && accumulator.push(flowRateVariant);
      return accumulator;
    }, [] as Symmons.Product[]);

  // this function returns all flow rate variants incuding the current product
  // if there's only one flow rate variant we only have the current product inside
  // we don't want to display flow rate variant picker in this case
  return flowRateVariants.length > 1 ? flowRateVariants : [];
}

function sthpGetDistinctFlowRateVariants(data: Symmons.ProductData) {
  const distinctVariants = Object.values(
    data.multivariants
      .filter((product): product is Symmons.Product & { flowRateId: string } => typeof product.flowRateId === 'string')
      .reduce((accumulator, product) => {
        accumulator[product.flowRateId] ??= product;
        return accumulator;
      }, {} as Record<string, Symmons.Product>)
  );

  return distinctVariants;
}

function sthpGetDistinctModifiedShowerTrimVariants(data: Symmons.ProductData) {
  const distinctVariants = Object.values(
    data.multivariants.reduce((accumulator, product) => {
      accumulator[product.showerTrimModifierId ?? sthpShowerTrimModifierIdNullKey] ??= product;
      return accumulator;
    }, {} as Record<string, Symmons.Product>)
  );

  distinctVariants.sort((product1, product2) =>
    compareShowerTrimModifierId(product1.showerTrimModifierId, product2.showerTrimModifierId)
  );

  return distinctVariants;
}

function sthpGetDistinctTestCapVariants(data: Symmons.ProductData) {
  const distinctVariants = Object.values(
    data.multivariants.reduce((accumulator, product) => {
      accumulator[product.isTestCap ? 'yes' : 'no'] ??= product;
      return accumulator;
    }, {} as Record<string, Symmons.Product>)
  );

  return distinctVariants;
}

function isTempControlDigitalHubProductData(
  productData: Symmons.ProductData
): productData is Symmons.TempControlDigitalHubProductData {
  return productData.type === 'tempcontrol-digital-hub-product';
}

function updatePage(product: Symmons.Product) {
  // where to buy

  const $whereToBuyBtns = jQuery('.js-product-single__where-to-buy-btn');
  $whereToBuyBtns.removeClass('is-active');
  $whereToBuyBtns.filter(`[ps-sku="${jQuery.escapeSelector(product.sku)}"]`).addClass('is-active');

  // primary preset content blocks

  const $primaryPresetContentBlocks = jQuery('.js-product-single__primary-preset-content-block');
  $primaryPresetContentBlocks.hide();
  $primaryPresetContentBlocks.filter(`[data-skus*="|${jQuery.escapeSelector(product.sku)}|"]`).show();

  // features

  const $featureBulletsBlock = jQuery('.js-product-single__feature-bullets-block');
  // const $featureBulletsList = jQuery('.js-product-single__feature-bullets-list');

  if (product.featureBullets.length) {
    // $featureBulletsList.empty();
    // product.featureBullets.forEach((featureBullet) => {
    //   $featureBulletsList.append(`
    //     <li>
    //       ${_escape(featureBullet)}
    //     </li>
    //   `);
    // });
    $featureBulletsBlock.show();
  } else {
    $featureBulletsBlock.hide();
  }

  // documentation

  const $documentationBlocks = jQuery('.js-product-single__documentation-block');
  $documentationBlocks.hide();
  $documentationBlocks.filter(`[data-skus*="|${jQuery.escapeSelector(product.sku)}|"]`).show();

  // replacement parts blocks

  const $replacementPartsBlocks = jQuery('.js-product-single__replacement-parts-block');
  $replacementPartsBlocks.hide();
  $replacementPartsBlocks.filter(`[data-skus*="|${jQuery.escapeSelector(product.sku)}|"]`).show();

  // secondary preset content blocks

  const $secondaryPresetContentBlocks = jQuery('.js-product-single__secondary-preset-content-block');
  $secondaryPresetContentBlocks.hide();
  $secondaryPresetContentBlocks.filter(`[data-skus*="|${jQuery.escapeSelector(product.sku)}|"]`).show();
}

interface DropdownProps<
  Options extends Record<string, { hide?: boolean; inactive?: boolean; option: React.ReactNode }>
> {
  label?: string;
  description: string;
  options: Options;
  value: keyof Options;
  onChange: (value: keyof Options) => void;
  disabled?: boolean;
  className?: string;
  hideEmptyKey?: boolean;
  dropdownPosition?: 'left' | 'right';
}

export function Dropdown<
  Options extends Record<string, { hide?: boolean; inactive?: boolean; option: React.ReactNode }>
>({
  label,
  description,
  options,
  value,
  onChange,
  disabled,
  className,
  hideEmptyKey,
  dropdownPosition = 'right',
}: DropdownProps<Options>) {
  const [isOpen, setIsOpen] = React.useState(false);
  const elRef = React.useRef<HTMLDivElement>(null);
  const toggle = React.useCallback<React.MouseEventHandler<HTMLAnchorElement | HTMLSpanElement>>(
    (event) => {
      event.preventDefault();
      if (disabled) {
        return;
      }
      setIsOpen((val) => !val);
    },
    [disabled]
  );
  useClickAway(elRef, () => {
    setIsOpen(false);
  });

  return (
    <div
      className={classNames(
        'c-dropdown-wrapper',
        { 'has-dropdown-open': isOpen, 'c-dropdown-wrapper--disabled': disabled },
        className
      )}
      ref={elRef}
    >
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <a href="#" className="c-tag c-tag--light c-tag--large c-dropdown-toggle" onClick={toggle}>
        {typeof label !== 'undefined' && <span className="c-dropdown-desc">{label}</span>}
        <span className="c-dropdown-value">{options[value].option}</span>
      </a>
      <div className={classNames('c-dropdown', `c-dropdown--${dropdownPosition}`)}>
        <div className="c-dropdown__header c-dropdown__header--sm">
          <span className="c-dropdown__title">{description}</span>
          <span className="c-dropdown__toggle" onClick={toggle}>
            <Icon name="close-thin" className="o-svg-icon" />
          </span>
        </div>
        <div className="c-dropdown__body">
          <ul className="c-dropdown__list">
            {Object.keys(options)
              .filter((optionKey) => {
                if (!optionKey && hideEmptyKey) {
                  return false;
                }

                if (options[optionKey].hide) {
                  return false;
                }

                return true;
              })
              .map((optionKey) => (
                <li
                  key={optionKey}
                  className={classNames({
                    'is-selected': value === optionKey,
                    'is-inactive': options[optionKey].inactive,
                  })}
                >
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <a
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      setIsOpen(false);
                      onChange(optionKey);
                    }}
                  >
                    {options[optionKey].option}
                  </a>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

interface TestCapToggleProps {
  name: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}

const TestCapToggle: React.VFC<TestCapToggleProps> = ({ name, checked, disabled, onChange }) => {
  return (
    <div className="c-form-element c-form-element--style-line c-form-element--toggle c-form-element--toggle--reverse">
      <div className="c-form-element__field">
        <input
          type="checkbox"
          name={name}
          id={name}
          checked={checked}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) {
              return;
            }
            onChange(e.target.checked);
          }}
        />
        <label htmlFor={name}></label>
      </div>
    </div>
  );
};

const ProductHeroGetInTouchButton: React.VFC<{ html: string }> = ({ html }) => {
  const elRef = React.useRef<HTMLDivElement>(null);

  useMount(() => {
    if (!elRef.current) {
      return;
    }

    jQuery(elRef.current)
      .find('.js-modal-base')
      // @ts-ignore
      .magnificPopup({
        closeOnContentClick: false,
        showCloseBtn: false,
      });
  });

  return (
    <div className="c-list-grid" ref={elRef}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
};

const DivOrFragment: React.VFC<React.HTMLAttributes<HTMLDivElement> & { isFragment?: boolean }> = ({
  isFragment,
  ...props
}) => {
  if (isFragment) {
    return <>{props.children}</>;
  }

  return <div {...props} />;
};

interface SectionData {
  enable_documentation_anchor: boolean;
  enable_faq_anchor: boolean;
  get_in_touch_modal_cta_html: string;
}

interface DefaultProductHeroProps {
  productData: Symmons.ProductData;
  sectionData: SectionData;
}

export const DefaultProductHero: React.VFC<DefaultProductHeroProps> = ({ productData, sectionData }) => {
  const auth = useAuth();
  const [currentProduct, setCurrentProduct] = React.useState(productData.currentProduct);
  const { title, isObsolete, isRetailOnly, isModified, isNew, sku, availabilityHtml, categories, business_segments } =
    currentProduct;
  const finishVariants = productData.multivariants
    .filter((product) => product.type === 'variant')
    .filter((product) => product.flowRateId === currentProduct.flowRateId)
    .filter((product): product is Symmons.Product & { finish: string } => typeof product.finish === 'string');
  const flowRateVariants = getCurrentFlowRateVariants(productData, currentProduct);
  const tempcontrolTierVariants = isTempControlDigitalHubProductData(productData)
    ? Object.values(productData.currentTempcontrolTierVariants ?? {})
    : [];
  const productMediaItems = productData.mediaItems[currentProduct.sku] || [];
  const changeProduct = React.useCallback((product: Symmons.Product) => {
    window.history.pushState(null, '', product.permalink);
    setCurrentProduct(product);
    updatePage(product);
  }, []);
  const changeMultivariant = React.useCallback(
    (flowRateId: string | null, finish: string | null) => {
      const product = getMultivariant(productData, flowRateId, finish);
      if (!product) {
        return;
      }
      changeProduct(product);
    },
    [productData, changeProduct]
  );

  return (
    <>
      <div className="o-container-fluid">
        <div className="o-row u-flex-grow">
          <div className="o-col-6@lg u-pr-gutter@lg">
            <div className="c-hero__content u-justify-around">
              <div className="c-block__header c-block__header--large">
                {categories.length > 0 && (
                  <p className="c-headline">{categories.map((category) => category.name).join(', ')}</p>
                )}
                <h1 className="c-title--extra-large">{title}</h1>
                <ul className="c-list-grid c-list-grid--small u-mt-spacer-base--small">
                  {(isObsolete || isRetailOnly || isModified || isNew || !!business_segments.length) && (
                    <li>
                      <div className="c-tag__group">
                        {isObsolete && <span className="c-tag c-tag--small">RETIRED</span>}
                        {isRetailOnly && <span className="c-tag c-tag--small c-tag--secondary-mild">Retail Only</span>}
                        {isModified && (
                          <a href="https://symmons.com?p=32563" className="c-tag c-tag--small c-tag--secondary">
                            Modified Shower Trim <Icon name="info" className="o-svg-icon" />
                          </a>
                        )}
                        {isNew && <span className="c-tag c-tag--small c-tag--secondary">New</span>}
                        {!!business_segments.length && (
                          <span className="c-tag c-tag--small c-tag--primary">Best Seller</span>
                        )}
                      </div>
                    </li>
                  )}
                  <li>
                    <p className="c-subtitle">Model {sku}</p>
                  </li>
                  <ProductIconLabels layout="hero" names={currentProduct.special_attributes ?? []} />
                </ul>
                {currentProduct.replacementForSku && currentProduct.replacementForSku.length > 0 && (
                  <div className="js-product-single__replacement_for_sku">
                    <div
                      className={
                        'c-product-replacement' +
                        (currentProduct.replacementForSku.length > 1 ? 'c-product-replacement--multi' : '')
                      }
                    >
                      <div className="c-product-replacement__notice">
                        <p>Updated Model</p>
                        <p>
                          This product has been retired; the following{' '}
                          {currentProduct.replacementForSku.length > 1 ? 'products are' : 'product is'} a recommended
                          replacement:
                        </p>
                      </div>
                      <div className="c-product-replacement__items">
                        {currentProduct.replacementForSku.map((replacement: any) => (
                          <div className="c-product-replacement__item" key={replacement.sku}>
                            <div className="c-product-replacement__info">
                              <div>
                                <p className="u-mb-0 u-text-sm">Model</p>
                                <p className="u-font-bold u-mb-0">{replacement.sku}</p>
                              </div>
                              <div className="c-color--white">
                                <a href={replacement.permalink} className="c-link-cta-basic">
                                  <span>View product</span>
                                  <Icon name="arrow" className="o-svg-icon o-svg-right" />
                                </a>
                              </div>
                            </div>
                            {replacement.mainImage && (
                              <div className="c-product-replacement__image">
                                <img src={replacement.mainImage} alt="#" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="c-product__features js-product-single__feature-bullets-block">
                <ul className="c-list-features js-product-single__feature-bullets-list">
                  {currentProduct.featureBullets.map((featureBullet) => (
                    <li key={featureBullet}>{featureBullet}</li>
                  ))}
                </ul>
              </div>

              <div className="c-product__info">
                <div className="c-list-grid u-items-end">
                  {availabilityHtml.length > 0 && (
                    <div>
                      <p className="c-subtitle" dangerouslySetInnerHTML={{ __html: availabilityHtml }} />
                    </div>
                  )}
                  <ProductHeroPrice
                    product={currentProduct}
                    small={!finishVariants.length && !flowRateVariants.length}
                  />
                </div>

                <div
                  className="c-product__options"
                  style={!finishVariants.length && !flowRateVariants.length ? { display: 'none' } : undefined}
                >
                  {finishVariants.length > 0 && (
                    <DivOrFragment
                      className={
                        finishVariants.length && flowRateVariants.length ? 'c-product__options-col' : undefined
                      }
                      isFragment={finishVariants.length && flowRateVariants.length ? false : true}
                    >
                      <div className="c-list-grid c-list-grid--small">
                        <div>
                          <p className="c-product__label">Finishes:</p>
                        </div>
                        <div>
                          <ul className="c-finishes c-finishes--hor">
                            {finishVariants.map((finish) => {
                              const finishIconUrl = wordpressData.finishIcons[finish.finish];

                              return (
                                <li
                                  className={classNames({ 'is-selected': finish.post_id === currentProduct.post_id })}
                                  key={finish.post_id}
                                >
                                  <a
                                    href={finish.permalink}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      changeMultivariant(currentProduct.flowRateId, finish.finish);
                                    }}
                                  >
                                    <span
                                      className="c-finishes__preview"
                                      style={{ backgroundImage: `url(${finishIconUrl})` }}
                                    ></span>
                                  </a>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </DivOrFragment>
                  )}

                  {flowRateVariants.length > 0 && (
                    <DivOrFragment
                      className={
                        finishVariants.length && flowRateVariants.length ? 'c-product__options-col' : undefined
                      }
                      isFragment={finishVariants.length && flowRateVariants.length ? false : true}
                    >
                      <div className="c-list-grid c-list-grid--small">
                        <div>
                          <p className="c-product__label">Flow Rate:</p>
                        </div>
                        <div>
                          <Dropdown
                            description="Flow Rate:"
                            options={
                              Object.fromEntries(
                                flowRateVariants.map((variant) => [
                                  variant.flowRateId!,
                                  { option: variant.flowRateName! },
                                ])
                              ) as unknown as Record<string, { option: string }>
                            }
                            value={currentProduct.flowRateId ?? flowRateVariants[0].flowRateId!}
                            onChange={(val) => {
                              changeMultivariant(val, currentProduct.finish);
                            }}
                          />
                        </div>
                      </div>
                    </DivOrFragment>
                  )}
                </div>

                {tempcontrolTierVariants.length > 0 && (
                  <div className="c-product__options">
                    <form className="u-mb-spacer u-flex-grow">
                      <div className="c-form-element c-form-element--radio c-form-element--small">
                        <label className="c-form-label c-form-label--cap u-hidden">Product Tier</label>
                        <div className="c-form-element__options c-form-element__options--row@md c-form-element__options--column@lg c-form-element__options--row@xl">
                          {tempcontrolTierVariants.map((tierVariant) => {
                            const tierInputId = `tempcontrol-tier-${tierVariant.id}-${tierVariant.product.post_id}`;
                            const isSelected = tierVariant.product.post_id === currentProduct.post_id;

                            return (
                              <div key={tierVariant.id} className="c-form-element__field c-form-element__field--clean">
                                <input
                                  type="radio"
                                  name="tempcontrol-tier"
                                  id={tierInputId}
                                  checked={isSelected}
                                  onChange={() => {
                                    if (!isSelected) {
                                      changeProduct(tierVariant.product);
                                    }
                                  }}
                                />
                                <label htmlFor={tierInputId}>
                                  {tierVariant.label}
                                  <span>{tierVariant.description}</span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </form>
                  </div>
                )}

                {auth.status === AuthStatus.LoggedOut ? (
                  <div className="c-list-grid">
                    <div>
                      <ProductHeroGetInTouchButton html={sectionData.get_in_touch_modal_cta_html} />
                    </div>
                    <div className="u-ml-auto">
                      <div className="c-list-grid">
                        <div>
                          {productData.multivariants
                            .filter(
                              (
                                multivariant
                              ): multivariant is Symmons.Product & { price: Symmons.Price; isObsolete: false } =>
                                multivariant.price !== null && !multivariant.isObsolete
                            )
                            .map((multivariant) => (
                              <div
                                key={multivariant.post_id}
                                className={classNames('ps-widget', 'c-button', 'c-button--primary', {
                                  'is-active': multivariant.sku === currentProduct.sku,
                                })}
                                ps-sku={multivariant.sku}
                              >
                                <span>Where to buy</span>
                                <Icon name="location" className="o-svg-icon o-svg-large" />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : auth.status === AuthStatus.LoggedIn &&
                  (auth.currentUser.role === AdminUserRoleEnum.Admin ||
                    auth.currentUser.role === SalesRepUserRoleEnum.SalesRep) ? (
                  <div className="c-list-grid">
                    <div></div>
                    <div className="u-ml-auto">
                      <div className="c-list-grid">
                        <div>
                          {productData.multivariants
                            .filter(
                              (
                                multivariant
                              ): multivariant is Symmons.Product & { price: Symmons.Price; isObsolete: false } =>
                                multivariant.price !== null && !multivariant.isObsolete
                            )
                            .map((multivariant) => (
                              <div
                                key={multivariant.post_id}
                                className={classNames('ps-widget', 'c-button', 'c-button--primary', {
                                  'is-active': multivariant.sku === currentProduct.sku,
                                })}
                                ps-sku={multivariant.sku}
                              >
                                <span>Where to buy</span>
                                <Icon name="location" className="o-svg-icon o-svg-large" />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                {!currentProduct.isObsolete && !currentProduct.isRetailOnly && (
                  <AddProductToQuote product={currentProduct} />
                )}
              </div>
            </div>
            <div className="c-hero__nav u-hidden u-flex@sm">
              <p>Skip to:</p>
              <ul>
                {sectionData.enable_documentation_anchor && (
                  <li>
                    <a href="#documentation">
                      <span>Documentation</span>
                    </a>
                  </li>
                )}
                {sectionData.enable_faq_anchor && (
                  <li>
                    <a href="#faq">
                      <span>FAQ</span>
                    </a>
                  </li>
                )}
                <li>
                  <a href="#request-more-information">
                    <span>Contact Us</span>
                  </a>
                </li>
              </ul>
            </div>
            <div className="c-block__pattern-circle c-block__pattern--light3 c-block__overlay--opacity-20 u-ml-gutter-@xl">
              <Icon name="pattern-circle" className="o-svg-icon" />
            </div>
          </div>
          <div className="o-col-6@lg u-justify-around u-pt-0 u-pb-0">
            <ProductHeroGallery productMediaItems={productMediaItems} currentProduct={currentProduct} />
          </div>
        </div>
      </div>
      <div className="c-block__overlay c-block__overlay--hero c-block__overlay--base-half c-block__overlay--opacity-100 u-ml-gutter u-hidden u-block@lg"></div>
    </>
  );
};

interface ShowerProductHeroProps {
  productData: Symmons.ProductData;
  sectionData: SectionData;
}

export const ShowerProductHero: React.VFC<ShowerProductHeroProps> = ({ productData, sectionData }) => {
  const auth = useAuth();
  const [currentProduct, setCurrentProduct] = React.useState(productData.currentProduct);
  const [attributes, setAttributes] = React.useState<ISthpMultivariantAttributes>({
    flowRateId: productData.currentProduct.flowRateId,
    finish: productData.currentProduct.finish,
    showerTrimModifierId: productData.currentProduct.showerTrimModifierId,
    isTestCap: productData.currentProduct.isTestCap,
  });
  const { title, isObsolete, isRetailOnly, isModified, isNew, sku, availabilityHtml, categories, business_segments } =
    currentProduct;
  const isSelectingVariant =
    attributes.flowRateId === sthpUndefinedAttributeKey ||
    attributes.finish === sthpUndefinedAttributeKey ||
    attributes.showerTrimModifierId === sthpUndefinedAttributeKey;

  const distinctFlowRateVariants = React.useMemo(() => sthpGetDistinctFlowRateVariants(productData), [productData]);
  const distinctFinishVariants = React.useMemo(() => sthpGetDistinctFinishVariants(productData), [productData]);
  const distinctModifiedShowerTrimVariants = React.useMemo(
    () => sthpGetDistinctModifiedShowerTrimVariants(productData),
    [productData]
  );
  const distinctTestCapVariants = React.useMemo(() => sthpGetDistinctTestCapVariants(productData), [productData]);

  const productMediaItems = productData.mediaItems[currentProduct.sku] || [];

  const changeMultivariant = (product: Symmons.Product) => {
    window.history.pushState(null, '', product.permalink);
    setCurrentProduct(product);
    setAttributes({
      flowRateId: product.flowRateId,
      finish: product.finish,
      showerTrimModifierId: product.showerTrimModifierId,
      isTestCap: product.isTestCap,
    });
    updatePage(product);
  };

  const flowRateDropdown = distinctFlowRateVariants.length ? (
    <div className="c-list-grid c-list-grid--small">
      <div>
        <p className="c-product__label">Flow Rate:</p>
      </div>
      <div>
        <Dropdown
          description="Flow Rate:"
          options={
            Object.fromEntries([
              ...(attributes.flowRateId === sthpUndefinedAttributeKey
                ? [[sthpUndefinedAttributeKey, { hide: true, option: <>Please choose</> }]]
                : []),
              ...distinctFlowRateVariants.map((product) => {
                const variant = sthpGetPartialMultivariant(productData, {
                  ...attributes,
                  flowRateId: product.flowRateId,
                });

                return [
                  product.flowRateId!,
                  {
                    inactive: !variant,
                    option: (
                      <>
                        {product.flowRateName}
                        {!variant && (
                          <>
                            {' '}
                            <Tippy content="Not available in this configuration">
                              <span
                                className="c-dropdown__list-info"
                                data-tippy-content="Not available in this configuration"
                              >
                                <SpriteIcon name="alert" classes={['o-svg-icon']} />
                              </span>
                            </Tippy>
                          </>
                        )}
                      </>
                    ),
                  },
                ];
              }),
            ]) as unknown as Record<string, { inactive: boolean; option: React.ReactNode }>
          }
          value={attributes.flowRateId!}
          onChange={(val) => {
            const updatedAttributes = { ...attributes, flowRateId: val };

            const exactVariant = sthpGetExactMultivariant(productData, updatedAttributes);
            if (exactVariant) {
              // setAttributes(updatedAttributes);
              changeMultivariant(exactVariant);
              return;
            }

            const partialVariant = sthpGetPartialMultivariant(productData, updatedAttributes);
            if (partialVariant) {
              setAttributes(updatedAttributes);
              return;
            }

            // find which test cap variant is available and switch test cap to that value
            const isSameTestCapVariantAvailable = !!sthpGetPartialMultivariant(productData, {
              ...attributes,
              flowRateId: val,
              finish: sthpUndefinedAttributeKey,
              showerTrimModifierId: sthpUndefinedAttributeKey,
            });

            setAttributes({
              flowRateId: val,
              finish: sthpUndefinedAttributeKey,
              showerTrimModifierId: sthpUndefinedAttributeKey,
              isTestCap: isSameTestCapVariantAvailable ? attributes.isTestCap : !attributes.isTestCap,
            });
          }}
        />
      </div>
    </div>
  ) : null;

  const finishDropdown = distinctFinishVariants.length ? (
    <div className="c-list-grid c-list-grid--small">
      <div>
        <p className="c-product__label">Finishes:</p>
      </div>
      <div>
        <ul className="c-finishes c-finishes--hor">
          {distinctFinishVariants.map((product) => {
            const finishIconUrl = wordpressData.finishIcons[product.finish!];

            const variant = sthpGetPartialMultivariant(productData, { ...attributes, finish: product.finish });

            return (
              <li
                className={classNames({
                  'is-selected': product.finish === attributes.finish,
                  'is-inactive': !variant,
                })}
                key={product.post_id}
              >
                <a
                  href={product.permalink}
                  onClick={(event) => {
                    event.preventDefault();

                    const updatedAttributes = { ...attributes, finish: product.finish };

                    const exactVariant = sthpGetExactMultivariant(productData, updatedAttributes);
                    if (exactVariant) {
                      // setAttributes(updatedAttributes);
                      changeMultivariant(exactVariant);
                      return;
                    }

                    const partialVariant = sthpGetPartialMultivariant(productData, updatedAttributes);
                    if (partialVariant) {
                      setAttributes(updatedAttributes);
                      return;
                    }

                    // find which test cap variant is available and switch test cap to that value
                    const isSameTestCapVariantAvailable = !!sthpGetPartialMultivariant(productData, {
                      ...attributes,
                      flowRateId: sthpUndefinedAttributeKey,
                      finish: product.finish,
                      showerTrimModifierId: sthpUndefinedAttributeKey,
                    });

                    setAttributes({
                      flowRateId: sthpUndefinedAttributeKey,
                      finish: product.finish,
                      showerTrimModifierId: sthpUndefinedAttributeKey,
                      isTestCap: isSameTestCapVariantAvailable ? attributes.isTestCap : !attributes.isTestCap,
                    });
                  }}
                >
                  {!variant && (
                    <Tippy content="Not available in this configuration">
                      <div className="c-finishes__tooltip">
                        <SpriteIcon name="alert" classes={['o-svg-icon']} />
                      </div>
                    </Tippy>
                  )}
                  <span className="c-finishes__preview" style={{ backgroundImage: `url(${finishIconUrl})` }}></span>
                </a>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  ) : null;

  const modifiedShowerTrimDropdown = distinctModifiedShowerTrimVariants.length ? (
    <div className="c-list-grid c-list-grid--small">
      <div>
        <p className="c-product__label">Shower Trims:</p>
      </div>
      <div>
        <Dropdown
          description="Shower Trims:"
          options={
            Object.fromEntries([
              ...(attributes.showerTrimModifierId === sthpUndefinedAttributeKey
                ? [[sthpUndefinedAttributeKey, { hide: true, option: <>Please choose</> }]]
                : []),
              ...distinctModifiedShowerTrimVariants.map((product) => {
                const variant = sthpGetPartialMultivariant(productData, {
                  ...attributes,
                  showerTrimModifierId: product.showerTrimModifierId,
                });

                return [
                  product.showerTrimModifierId ?? sthpShowerTrimModifierIdNullKey,
                  {
                    inactive: !variant,
                    option: (
                      <>
                        {product.showerTrimModifierName ?? 'No Modification'}
                        {!variant && (
                          <>
                            {' '}
                            <Tippy content="Not available in this configuration">
                              <span
                                className="c-dropdown__list-info"
                                data-tippy-content="Not available in this configuration"
                              >
                                <SpriteIcon name="alert" classes={['o-svg-icon']} />
                              </span>
                            </Tippy>
                          </>
                        )}
                      </>
                    ),
                  },
                ];
              }),
            ]) as unknown as Record<string, { inactive: boolean; option: React.ReactNode }>
          }
          value={attributes.showerTrimModifierId ?? sthpShowerTrimModifierIdNullKey}
          onChange={(val) => {
            const updatedAttributes = {
              ...attributes,
              showerTrimModifierId: val === sthpShowerTrimModifierIdNullKey ? null : val,
            };

            const exactVariant = sthpGetExactMultivariant(productData, updatedAttributes);
            if (exactVariant) {
              // setAttributes(updatedAttributes);
              changeMultivariant(exactVariant);
              return;
            }

            const partialVariant = sthpGetPartialMultivariant(productData, updatedAttributes);
            if (partialVariant) {
              setAttributes(updatedAttributes);
              return;
            }

            // find which test cap variant is available and switch test cap to that value
            const isSameTestCapVariantAvailable = !!sthpGetPartialMultivariant(productData, {
              ...attributes,
              flowRateId: sthpUndefinedAttributeKey,
              finish: sthpUndefinedAttributeKey,
              showerTrimModifierId: val === sthpShowerTrimModifierIdNullKey ? null : val,
            });

            setAttributes({
              flowRateId: sthpUndefinedAttributeKey,
              finish: sthpUndefinedAttributeKey,
              showerTrimModifierId: val === sthpShowerTrimModifierIdNullKey ? null : val,
              isTestCap: isSameTestCapVariantAvailable ? attributes.isTestCap : !attributes.isTestCap,
            });
          }}
        />
      </div>
    </div>
  ) : null;

  const testCapToggle = distinctTestCapVariants.length ? (
    <div className="c-list-grid c-list-grid--small">
      <div>
        <p className="c-product__label">Test Cap:</p>
      </div>
      <div>
        <TestCapToggle
          name="product-test-cap"
          checked={attributes.isTestCap}
          disabled={distinctTestCapVariants.length === 1}
          onChange={(val) => {
            console.log(val);

            const updatedAttributes = { ...attributes, isTestCap: val };

            const exactVariant = sthpGetExactMultivariant(productData, updatedAttributes);
            if (exactVariant) {
              // setAttributes(updatedAttributes);
              changeMultivariant(exactVariant);
              return;
            }

            const partialVariant = sthpGetPartialMultivariant(productData, updatedAttributes);
            if (partialVariant) {
              setAttributes(updatedAttributes);
              return;
            }

            setAttributes({
              flowRateId: sthpUndefinedAttributeKey,
              finish: sthpUndefinedAttributeKey,
              showerTrimModifierId: sthpUndefinedAttributeKey,
              isTestCap: val,
            });
          }}
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      <div className="o-container-fluid">
        <div className="o-row u-flex-grow">
          <div className="o-col-6@lg u-pr-gutter@lg">
            <div className="c-hero__content u-justify-around">
              <div className="c-block__header c-block__header--large">
                {categories.length > 0 && (
                  <p className="c-headline">{categories.map((category) => category.name).join(', ')}</p>
                )}
                <h1 className="c-title--extra-large">{title}</h1>
                <ul className="c-list-grid c-list-grid--small u-mt-spacer-base--small">
                  {(isObsolete || isRetailOnly || isModified || isNew || !!business_segments.length) && (
                    <li>
                      <div className="c-tag__group">
                        {isObsolete && <span className="c-tag c-tag--small">RETIRED</span>}
                        {isRetailOnly && <span className="c-tag c-tag--small c-tag--secondary-mild">Retail Only</span>}
                        {isModified && (
                          <a href="https://symmons.com?p=32563" className="c-tag c-tag--small c-tag--secondary">
                            Modified Shower Trim <Icon name="info" className="o-svg-icon" />
                          </a>
                        )}
                        {isNew && <span className="c-tag c-tag--small c-tag--secondary">New</span>}
                        {!!business_segments.length && (
                          <span className="c-tag c-tag--small c-tag--primary">Best Seller</span>
                        )}
                      </div>
                    </li>
                  )}
                  <li>
                    <p className="c-subtitle">Model {sku}</p>
                  </li>
                  <ProductIconLabels layout="hero" names={currentProduct.special_attributes ?? []} />
                </ul>
                {currentProduct.replacementForSku && currentProduct.replacementForSku.length > 0 && (
                  <div className="js-product-single__replacement_for_sku">
                    <div
                      className={
                        'c-product-replacement' +
                        (currentProduct.replacementForSku.length > 1 ? 'c-product-replacement--multi' : '')
                      }
                    >
                      <div className="c-product-replacement__notice">
                        <p>Updated Model</p>
                        <p>
                          This product has been retired; the following{' '}
                          {currentProduct.replacementForSku.length > 1 ? 'products are' : 'product is'} a recommended
                          replacement:
                        </p>
                      </div>
                      <div className="c-product-replacement__items">
                        {currentProduct.replacementForSku.map((replacement: any) => (
                          <div className="c-product-replacement__item" key={replacement.sku}>
                            <div className="c-product-replacement__info">
                              <div>
                                <p className="u-mb-0 u-text-sm">Model</p>
                                <p className="u-font-bold u-mb-0">{replacement.sku}</p>
                              </div>
                              <div className="c-color--white">
                                <a href={replacement.permalink} className="c-link-cta-basic">
                                  <span>View product</span>
                                  <Icon name="arrow" className="o-svg-icon o-svg-right" />
                                </a>
                              </div>
                            </div>
                            {replacement.mainImage && (
                              <div className="c-product-replacement__image">
                                <img src={replacement.mainImage} alt="#" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="c-product__features js-product-single__feature-bullets-block">
                <ul className="c-list-features js-product-single__feature-bullets-list">
                  {currentProduct.featureBullets.map((featureBullet) => (
                    <li key={featureBullet}>{featureBullet}</li>
                  ))}
                </ul>
              </div>

              <div className="c-product__info">
                <div className="c-list-grid u-items-end">
                  {availabilityHtml.length > 0 && (
                    <div>
                      <p className="c-subtitle" dangerouslySetInnerHTML={{ __html: availabilityHtml }} />
                    </div>
                  )}
                  <ProductHeroPrice
                    product={currentProduct}
                    small={!distinctFinishVariants.length && !distinctFlowRateVariants.length}
                  />
                </div>

                <div
                  className="c-product__options u-px-0 u-flex-column u-flex-row@md u-flex-column@lg u-flex-row@xl"
                  style={
                    !distinctFlowRateVariants.length &&
                    !distinctFinishVariants.length &&
                    !distinctModifiedShowerTrimVariants.length &&
                    !distinctTestCapVariants.length
                      ? { display: 'none' }
                      : undefined
                  }
                >
                  {(distinctFinishVariants.length > 0 || distinctTestCapVariants.length > 0) && (
                    <div className="c-product__options-colgroup">
                      {distinctFinishVariants.length > 0 && (
                        <div className="c-product__options-col">{finishDropdown}</div>
                      )}
                      {distinctTestCapVariants.length > 0 && (
                        <div className="c-product__options-col">{testCapToggle}</div>
                      )}
                    </div>
                  )}
                  {(distinctFlowRateVariants.length > 0 || distinctModifiedShowerTrimVariants.length > 0) && (
                    <div className="c-product__options-colgroup">
                      {distinctFlowRateVariants.length > 0 && (
                        <div className="c-product__options-col">{flowRateDropdown}</div>
                      )}
                      {distinctModifiedShowerTrimVariants.length > 0 && (
                        <div className="c-product__options-col">{modifiedShowerTrimDropdown}</div>
                      )}
                    </div>
                  )}
                </div>

                {isSelectingVariant && (
                  <div className="c-info c-info--basic u-mb-spacer-base">
                    <div className="c-info__icon">
                      <SpriteIcon name="info" classes={['o-svg-icon']} />
                    </div>
                    <div className="c-info__body">
                      <div className="c-info__content">
                        <p>
                          The configuration you’ve selected isn’t available due to compatibility constraints within the
                          chosen features. Please review the other options and adjust your selections to continue.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {auth.status === AuthStatus.LoggedOut ? (
                  <div className="c-list-grid">
                    <div>
                      <ProductHeroGetInTouchButton html={sectionData.get_in_touch_modal_cta_html} />
                    </div>
                    <div className="u-ml-auto">
                      <div className="c-list-grid">
                        <div>
                          {productData.multivariants
                            .filter(
                              (
                                multivariant
                              ): multivariant is Symmons.Product & { price: Symmons.Price; isObsolete: false } =>
                                multivariant.price !== null && !multivariant.isObsolete
                            )
                            .map((multivariant) => (
                              <div
                                key={multivariant.post_id}
                                className={classNames('ps-widget', 'c-button', 'c-button--primary', {
                                  'is-active': multivariant.sku === currentProduct.sku,
                                })}
                                ps-sku={multivariant.sku}
                              >
                                <span>Where to buy</span>
                                <Icon name="location" className="o-svg-icon o-svg-large" />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : auth.status === AuthStatus.LoggedIn &&
                  (auth.currentUser.role === AdminUserRoleEnum.Admin ||
                    auth.currentUser.role === SalesRepUserRoleEnum.SalesRep) ? (
                  <div className="c-list-grid">
                    <div></div>
                    <div className="u-ml-auto">
                      <div className="c-list-grid">
                        <div>
                          {productData.multivariants
                            .filter(
                              (
                                multivariant
                              ): multivariant is Symmons.Product & { price: Symmons.Price; isObsolete: false } =>
                                multivariant.price !== null && !multivariant.isObsolete
                            )
                            .map((multivariant) => (
                              <div
                                key={multivariant.post_id}
                                className={classNames('ps-widget', 'c-button', 'c-button--primary', {
                                  'is-active': multivariant.sku === currentProduct.sku,
                                })}
                                ps-sku={multivariant.sku}
                              >
                                <span>Where to buy</span>
                                <Icon name="location" className="o-svg-icon o-svg-large" />
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
                {!currentProduct.isObsolete && !currentProduct.isRetailOnly && (
                  <AddProductToQuote product={currentProduct} />
                )}
              </div>
            </div>
            <div className="c-hero__nav u-hidden u-flex@sm">
              <p>Skip to:</p>
              <ul>
                {sectionData.enable_documentation_anchor && (
                  <li>
                    <a href="#documentation">
                      <span>Documentation</span>
                    </a>
                  </li>
                )}
                {sectionData.enable_faq_anchor && (
                  <li>
                    <a href="#faq">
                      <span>FAQ</span>
                    </a>
                  </li>
                )}
                <li>
                  <a href="#request-more-information">
                    <span>Contact Us</span>
                  </a>
                </li>
              </ul>
            </div>
            <div className="c-block__pattern-circle c-block__pattern--light3 c-block__overlay--opacity-20 u-ml-gutter-@xl">
              <Icon name="pattern-circle" className="o-svg-icon" />
            </div>
          </div>
          <div className="o-col-6@lg u-justify-around u-pt-0 u-pb-0">
            <ProductHeroGallery productMediaItems={productMediaItems} currentProduct={currentProduct} />
          </div>
        </div>
      </div>
      <div className="c-block__overlay c-block__overlay--hero c-block__overlay--base-half c-block__overlay--opacity-100 u-ml-gutter u-hidden u-block@lg"></div>
    </>
  );
};

export const ProductHero: React.VFC = () => {
  const productData = React.useMemo<Symmons.ProductData>(() => jQuery('body').data('product-single-data'), []);
  const sectionData = React.useMemo<{
    enable_documentation_anchor: boolean;
    enable_faq_anchor: boolean;
    get_in_touch_modal_cta_html: string;
  }>(() => jQuery('#product-hero-app').data('section-args'), []);

  if (productData.type === 'shower-trim-hub-product') {
    return <ShowerProductHero productData={productData} sectionData={sectionData} />;
  }

  return <DefaultProductHero productData={productData} sectionData={sectionData} />;
};
